import { useEffect, useState } from "react";
import { Plus, RefreshCw, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCrm } from "@/context/CrmContext";
import { MetricBar } from "@/components/MetricBar";
import { LeadCard } from "@/components/LeadCard";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { Skeleton } from "@/components/ui/skeleton";

export default function Feed() {
  const { user } = useAuth();
  const { tick, refresh } = useCrm();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openForm, setOpenForm] = useState(false);
  const [cached, setCached] = useState(false);

  useEffect(() => {
    api.get("/feed", { params: { tz_offset: new Date().getTimezoneOffset() } })
      .then((r) => { setData(r.data); setCached(r.headers["x-sw-cache"] === "1"); })
      .catch(() => setCached(true));
  }, [tick]);
  const leads = !data ? [] : filter === "all" ? data.leads : data.leads.filter((l) => l.flags[filter]);
  const hour = new Date().getHours();

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#1F9E7A]">{new Date().toDateString()}</p>
          <h1 className="font-display mt-1 text-2xl font-bold text-[#151515] sm:text-3xl">Good {hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening"}, {user.name.split(" ")[0]}</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="feed-refresh-btn" onClick={refresh} className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 text-neutral-500 hover:text-neutral-800"><RefreshCw className="h-4 w-4" /></button>
          <button data-testid="add-lead-btn" onClick={() => setOpenForm(true)} className="flex h-10 items-center gap-1.5 rounded-full bg-[#1F9E7A] px-4 text-sm font-semibold text-white hover:bg-[#178566]"><Plus className="h-4 w-4" /><span className="hidden sm:inline">New lead</span></button>
        </div>
      </div>
      {cached && (
        <div data-testid="feed-offline-banner" className="flex items-center gap-2 rounded-2xl bg-[#232323] px-4 py-3 text-sm text-white">
          <WifiOff className="h-4 w-4 text-[#F5E04F]" />Offline — showing your last synced queue{data?.generated_at ? ` (${new Date(data.generated_at).toLocaleTimeString()})` : ""}. Calls and WhatsApp still work.
        </div>
      )}
      <MetricBar metrics={data?.metrics} total={data?.leads.length ?? 0} active={filter} onSelect={setFilter} />
      <div data-testid="lead-feed" className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {!data && [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-2xl bg-neutral-100/60" />)}
        {data && leads.length === 0 && (
          <div data-testid="feed-empty" className="col-span-full rounded-2xl border border-dashed border-neutral-200 p-10 text-center text-neutral-500">Queue clear for this filter. Nice work.</div>
        )}
        {leads.map((l, i) => <LeadCard key={l.id} lead={l} index={i} />)}
      </div>
      <LeadFormDialog open={openForm} onOpenChange={setOpenForm} onCreated={refresh} />
    </div>
  );
}
